// src/services/productionPlanning.ts
//
// Phase 2C-lite planning math. Given a forecast (list of {productId, qty}),
// computes:
//   - required batches per recipe
//   - required total quantity per ingredient item
//   - shortage = max(0, required - onHand)
//   - cost to procure shortages
//
// No date math, no time-bucketing — a single horizon. Phase 3 will add
// date-aware planning + multi-period rollups.

import type {
  ProductionItem,
  ProductionRecipe,
  ProductionProduct,
  ForecastLine,
  RequiredBatch,
  RequiredItem,
} from '../types/production';

export interface PlanningResult {
  /** Lines from the forecast that couldn't be planned (deleted product or recipe). */
  unresolved: Array<{ forecastLineId: string; reason: string }>;
  requiredBatches: RequiredBatch[];
  requiredItems: RequiredItem[];
  /** Sum of shortage × costPerUnit across all items with a shortfall. */
  totalProcurementCost: number;
}

export function planForecast(
  forecast: ForecastLine[],
  productsById: Record<string, ProductionProduct>,
  recipesById: Record<string, ProductionRecipe>,
  itemsById: Record<string, ProductionItem>,
): PlanningResult {
  // Step 1: aggregate batches per recipe
  const batchesByRecipe = new Map<string, { recipe: ProductionRecipe; batches: number }>();
  const unresolved: PlanningResult['unresolved'] = [];

  for (const line of forecast) {
    const product = productsById[line.productId];
    if (!product) {
      unresolved.push({ forecastLineId: line.id, reason: 'product no longer exists' });
      continue;
    }
    const recipe = recipesById[product.recipeId];
    if (!recipe) {
      unresolved.push({ forecastLineId: line.id, reason: `product's recipe (${product.recipeId}) no longer exists` });
      continue;
    }
    // One product unit consumes `unitsPerBatch` of recipe yield. So:
    //   batches needed = (forecast units) × unitsPerBatch ÷ recipe yield
    const unitsOfRecipeYield = (line.qty || 0) * (product.unitsPerBatch || 1);
    const batches = unitsOfRecipeYield / (recipe.yieldQty || 1);

    const existing = batchesByRecipe.get(recipe.id);
    if (existing) {
      existing.batches += batches;
    } else {
      batchesByRecipe.set(recipe.id, { recipe, batches });
    }
  }

  const requiredBatches: RequiredBatch[] = [];
  for (const { recipe, batches } of batchesByRecipe.values()) {
    requiredBatches.push({
      recipeId: recipe.id,
      recipeName: recipe.name,
      batchesNeeded: batches,
      totalRecipeYield: batches * (recipe.yieldQty || 1),
    });
  }
  requiredBatches.sort((a, b) => b.batchesNeeded - a.batchesNeeded);

  // Step 2: aggregate items per recipe × component
  const qtyByItem = new Map<string, number>();
  for (const { recipe, batches } of batchesByRecipe.values()) {
    for (const c of recipe.components) {
      const additional = batches * (c.qty || 0);
      qtyByItem.set(c.itemId, (qtyByItem.get(c.itemId) || 0) + additional);
    }
  }

  // Step 3: shortage + procurement cost
  const requiredItems: RequiredItem[] = [];
  let totalProcurementCost = 0;
  for (const [itemId, qtyNeeded] of qtyByItem.entries()) {
    const item = itemsById[itemId];
    if (!item) {
      requiredItems.push({
        itemId,
        itemName: '(missing item)',
        unit: '',
        qtyNeeded,
        onHand: 0,
        shortage: qtyNeeded,
        costToProcure: 0,
      });
      continue;
    }
    const onHand = item.onHand ?? 0;
    const shortage = Math.max(0, qtyNeeded - onHand);
    const cost = shortage * (item.costPerUnit || 0);
    totalProcurementCost += cost;
    requiredItems.push({
      itemId,
      itemName: item.name,
      unit: item.unit,
      qtyNeeded,
      onHand,
      shortage,
      costToProcure: cost,
    });
  }
  requiredItems.sort((a, b) => b.shortage - a.shortage);

  return {
    unresolved,
    requiredBatches,
    requiredItems,
    totalProcurementCost,
  };
}
